import { useState, useEffect, useRef } from 'react'
import { UserProfile } from '../../types'
import { apiService } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'

interface UserDetailViewProps {
  user: UserProfile
  onClose: () => void
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{value || '—'}</dd>
    </div>
  )
}

export function EmailField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">
        {value ? (
          <a href={`mailto:${value}`} className="text-blue-600 hover:text-blue-800 hover:underline">
            {value}
          </a>
        ) : (
          '—'
        )}
      </dd>
    </div>
  )
}

export function PhoneField({ label, value }: { label: string; value?: string | null }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">
        {value ? (
          <div className="relative inline-block" ref={ref}>
            <button
              type="button"
              onClick={() => setOpen((prev) => !prev)}
              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
            >
              {value}
            </button>
            {open && (
              <div className="absolute left-0 mt-1 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]">
                <a
                  href={`tel:${value}`}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setOpen(false)}
                >
                  📞 Call
                </a>
                <a
                  href={`sms:${value}`}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setOpen(false)}
                >
                  💬 SMS
                </a>
              </div>
            )}
          </div>
        ) : (
          '—'
        )}
      </dd>
    </div>
  )
}

const ROLE_OPTIONS: { value: UserProfile['role']; label: string }[] = [
  { value: 'pilot', label: 'Pilot' },
  { value: 'photo_admin', label: 'Photo Admin' },
  { value: 'admin', label: 'Admin' },
]

const ROLE_BADGE_COLORS: Record<string, string> = {
  admin: 'bg-red-100 text-red-800',
  photo_admin: 'bg-purple-100 text-purple-800',
  pilot: 'bg-blue-100 text-blue-800',
}

function RoleManager({
  user,
  disabled,
  onToast,
}: {
  user: UserProfile
  disabled: boolean
  onToast: (toast: { message: string; type: 'success' | 'error' }) => void
}) {
  const [editing, setEditing] = useState(false)
  const [selectedRole, setSelectedRole] = useState<UserProfile['role']>(user.role)
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!editing) return
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setEditing(false)
        setConfirming(false)
        setSelectedRole(user.role)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [editing, user.role])

  const handleSelectRole = (role: UserProfile['role']) => {
    if (role === user.role) {
      setEditing(false)
      return
    }
    setSelectedRole(role)
    setConfirming(true)
  }

  const handleConfirm = async () => {
    setSaving(true)
    const response = await apiService.updateUserRole(user.userId, selectedRole)
    if (response.error) {
      onToast({ message: response.error.message || 'Failed to update role', type: 'error' })
    } else {
      onToast({ message: `Role updated to ${selectedRole}`, type: 'success' })
      user.role = selectedRole
    }
    setSaving(false)
    setConfirming(false)
    setEditing(false)
  }

  const handleCancel = () => {
    setConfirming(false)
    setSelectedRole(user.role)
    setEditing(false)
  }

  const badgeColor = ROLE_BADGE_COLORS[user.role] || 'bg-gray-100 text-gray-800'

  return (
    <div className="relative inline-flex items-center gap-2" ref={dropdownRef}>
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
        {user.role}
      </span>
      {!disabled && !editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs text-gray-500 hover:text-gray-700 underline"
        >
          Edit Role
        </button>
      )}
      {disabled && (
        <span className="text-xs text-gray-400 italic">Your account</span>
      )}
      {editing && !confirming && (
        <div className="absolute top-full left-0 mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]">
          {ROLE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleSelectRole(opt.value)}
              className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                opt.value === user.role ? 'font-semibold text-gray-900' : 'text-gray-700'
              }`}
            >
              {opt.label}
              {opt.value === user.role && ' (current)'}
            </button>
          ))}
        </div>
      )}
      {confirming && (
        <div className="absolute top-full left-0 mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[240px]">
          <p className="text-sm text-gray-700 mb-3">
            Change role from <span className="font-semibold">{user.role}</span> to{' '}
            <span className="font-semibold">{selectedRole}</span>?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={saving}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-2">{title}</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {children}
      </dl>
    </div>
  )
}

export default function UserDetailView({ user, onClose }: UserDetailViewProps) {
  const { user: currentUser } = useAuth()
  const [resending, setResending] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [headshotUrl, setHeadshotUrl] = useState<string | null>(null)
  const isSelf = currentUser?.userId === user.userId

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  // Fetch presigned headshot URL when component mounts
  useEffect(() => {
    if (!user.headshotKey) return
    
    async function fetchHeadshotUrl() {
      const response = await apiService.listHeadshots()
      if (response.data) {
        const data = (response.data as any)?.data || response.data
        const headshots = data?.headshots || []
        const match = headshots.find((h: any) => h.key === user.headshotKey)
        if (match?.thumbnailUrl) {
          setHeadshotUrl(match.thumbnailUrl)
        }
      }
    }
    fetchHeadshotUrl()
  }, [user.headshotKey])

  const handleResendInvite = async () => {
    setResending(true)
    const response = await apiService.resendInvite(user.userId)
    if (response.error) {
      setToast({ message: response.error.message, type: 'error' })
    } else {
      setToast({ message: `Invite email sent to ${user.email}`, type: 'success' })
    }
    setResending(false)
  }

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          <div className="flex items-center gap-2">
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-white/80 hover:text-white">&times;</button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-start">
        <div className="flex items-start gap-4">
          {/* Headshot thumbnail or placeholder avatar */}
          {headshotUrl ? (
            <img
              src={headshotUrl}
              alt={`${user.firstName} ${user.lastName}`}
              className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
              onError={(e) => {
                // Hide broken image and show placeholder
                const target = e.currentTarget
                target.style.display = 'none'
                const placeholder = target.nextElementSibling as HTMLElement
                if (placeholder) placeholder.style.display = 'flex'
              }}
            />
          ) : null}
          <div
            className={`w-16 h-16 rounded-full bg-gray-100 flex-shrink-0 items-center justify-center ${headshotUrl ? 'hidden' : 'flex'}`}
          >
            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-bold text-gray-900">
              {user.firstName} {user.lastName}
            </h2>
            <RoleManager user={user} disabled={isSelf} onToast={setToast} />
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleResendInvite}
            disabled={resending}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resending ? 'Sending...' : 'Resend Invite'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            ← Back to list
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-8">
        <Section title="Personal Information">
          <Field label="Name" value={`${user.firstName} ${user.lastName}`} />
          <Field label="Date of Birth" value={user.dateOfBirth} />
          <Field label="Gender" value={user.gender} />
          <Field label="Name in National Alphabet" value={user.nameInNationalAlphabet} />
          <Field label="Blood Type" value={user.bloodType} />
          <Field label="T-Shirt Size" value={user.tshirtSize} />
          <Field label="Food Choice" value={user.foodChoice} />
        </Section>

        <Section title="Contact Details">
          <EmailField label="Email" value={user.email} />
          <PhoneField label="Mobile Tel" value={user.mobileTelNumber} />
          <PhoneField label="WhatsApp" value={user.whatsappNumber} />
          <Field label="Address" value={user.address} />
          <Field label="City" value={user.city} />
          <Field label="Postal Code" value={user.postalCode} />
          <Field label="Administrative Region" value={user.administrativeRegion} />
          <Field label="Country of Residence" value={user.countryOfResidence} />
        </Section>

        <Section title="Competition Details">
          <Field label="Participant Number" value={user.participantNumber} />
          <Field label="Confirmation Status" value={user.confirmationStatus} />
          <Field label="Registration Time" value={user.registrationTime} />
          <Field label="SRS ID" value={user.srsId} />
          <Field label="SRS Ranking" value={user.srsRanking} />
          <Field label="Ranking at Req Date" value={user.rankingAtReqDate} />
          <Field label="Latest Ranking" value={user.latestRanking} />
          <Field label="FAI Nation" value={user.faiNation} />
          <Field label="FAI License Number" value={user.faiLicenseNumber} />
          <Field label="CIVL ID" value={user.civlId} />
          <Field label="National Federation License ID" value={user.nationalFederationLicenseId} />
          <Field label="Club" value={user.club} />
          <Field label="Team" value={user.team} />
          <Field label="Sponsor" value={user.sponsor} />
        </Section>

        <Section title="Equipment">
          <Field label="Paraglider Manufacturer" value={user.paragliderManufacturer} />
          <Field label="Paraglider Model" value={user.paragliderModel} />
          <Field label="Paraglider Color" value={user.paragliderColor} />
          <Field label="Paraglider Certification" value={user.paragliderCertification} />
          <Field label="Glider Size" value={user.gliderSize} />
          <Field label="Max Rated Range (kg)" value={user.maximumRatedRangeKg} />
          <Field label="Harness Manufacturer" value={user.harnessManufacturer} />
          <Field label="Harness Model" value={user.harnessModel} />
          <Field label="Tracker" value={user.tracker} />
          <Field label="Trackserial" value={user.trackserial} />
        </Section>

        <Section title="Insurance">
          <Field label="Company" value={user.insuranceCompany} />
          <Field label="Policy Number" value={user.insurancePolicyNumber} />
          <PhoneField label="Phone Number" value={user.insurancePhoneNumber} />
        </Section>

        <Section title="Emergency Contact">
          <Field label="Contact Name" value={user.emergencyContactName} />
          <PhoneField label="Contact Phone" value={user.emergencyContactPhone} />
        </Section>

        <Section title="Accommodation">
          <Field label="Accommodation During Event" value={user.accommodationDuringEvent} />
        </Section>
      </div>
    </div>
  )
}
